import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import VideoFileRoundedIcon from '@mui/icons-material/VideoFileRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import { DISPATCH_EVENT_KEYS, SIGNALING_COMMANDS } from 'src/consumer/constants'
import { addWindowSignalingEventListener } from 'src/live'
import {
  getSupportAgentContactCapabilities,
  getSupportAgentWhatsappThread,
  getSupportAgentWhatsappThreads,
  prepareSupportAgentEmail,
  sendSupportAgentWhatsappAttachment,
  sendSupportAgentWhatsappMessage,
  sendSupportAgentWhatsappTemplate,
} from 'src/supportPortal/services/supportAgentContactService'

function parseThreadTimestamp(value) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const numericDate = new Date(value < 1e12 ? value * 1000 : value)
    return Number.isNaN(numericDate.getTime()) ? null : numericDate
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    if (/^\d{10,13}$/.test(trimmed)) {
      const numericValue = Number(trimmed)
      const numericDate = new Date(trimmed.length <= 10 ? numericValue * 1000 : numericValue)
      return Number.isNaN(numericDate.getTime()) ? null : numericDate
    }
    const directDate = new Date(trimmed)
    if (!Number.isNaN(directDate.getTime())) {
      return directDate
    }
    const normalizedDate = new Date(trimmed.replace(' ', 'T'))
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate
  }

  if (typeof value === 'object') {
    if (value?.$date) {
      return parseThreadTimestamp(value.$date)
    }
    if (value?.epochSecond !== undefined) {
      const milliseconds = (Number(value.epochSecond) * 1000) + Math.floor(Number(value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.seconds !== undefined) {
      const milliseconds = (Number(value.seconds) * 1000) + Math.floor(Number(value.nanos || value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.time !== undefined) {
      return parseThreadTimestamp(value.time)
    }
    if (value?.value !== undefined) {
      return parseThreadTimestamp(value.value)
    }
  }

  return null
}

function formatThreadTimestamp(value) {
  const date = parseThreadTimestamp(value)
  if (!date) {
    return ''
  }

  const now = new Date()
  const isSameYear = now.getFullYear() === date.getFullYear()
  const isSameDay = now.getFullYear() === date.getFullYear()
    && now.getMonth() === date.getMonth()
    && now.getDate() === date.getDate()

  return new Intl.DateTimeFormat('en-IN', {
    ...(isSameDay ? {} : { day: 'numeric', month: 'short' }),
    ...(isSameYear || isSameDay ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function getTemplateActionFriendlyLabel(actionType, fallbackLabel) {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (actionType === 'permission-template') {
    return 'Conversation opener'
  }
  if (actionType === 'call-heads-up') {
    return 'Call heads-up'
  }
  if (actionType === 'inbound-reply') {
    return 'Reply'
  }
  if (actionType === 'free-form') {
    return 'Message'
  }
  return 'Interaction'
}

function getTemplateActionChipColor(actionType) {
  if (actionType === 'call-heads-up') {
    return 'warning'
  }
  if (actionType === 'permission-template') {
    return 'info'
  }
  if (actionType === 'inbound-reply') {
    return 'secondary'
  }
  return 'default'
}

function getDeliveryStatusMeta(status) {
  const normalized = `${status || ''}`.toLowerCase()
  if (normalized === 'read') {
    return {
      label: 'Read',
      color: '#2563eb',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'delivered') {
    return {
      label: 'Delivered',
      color: '#0f766e',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'sent' || normalized === 'queued' || normalized === 'accepted') {
    return {
      label: normalized === 'queued' ? 'Queued' : 'Sent',
      color: '#475569',
      icon: normalized === 'queued'
        ? <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
        : <DoneRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'failed' || normalized === 'undelivered') {
    return {
      label: normalized === 'undelivered' ? 'Undelivered' : 'Failed',
      color: '#dc2626',
      icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />,
    }
  }
  return {
    label: status || 'Sending',
    color: '#475569',
    icon: <ScheduleRoundedIcon sx={{ fontSize: 15 }} />,
  }
}

function getAttachmentIcon(kind) {
  if (kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: 18 }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: 18 }} />
}

const WHATSAPP_ATTACHMENT_OPTIONS = [
  {
    id: 'media',
    label: 'Photos & videos',
    helper: 'Images and video clips',
    accept: 'image/*,video/*',
    kind: 'media',
  },
  {
    id: 'document',
    label: 'Document',
    helper: 'PDF, DOC, XLS and more',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip,.rar,.7z',
    kind: 'document',
  },
  {
    id: 'audio',
    label: 'Audio',
    helper: 'Voice notes and audio files',
    accept: 'audio/*',
    kind: 'audio',
  },
  {
    id: 'all',
    label: 'Any file',
    helper: 'Fallback for other attachments',
    accept: '*/*',
    kind: 'document',
  },
]

function getComposerAttachmentIcon(kind, size = 18) {
  if (kind === 'media' || kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: size }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: size }} />
}

function getPendingAttachmentKind(file) {
  const mimeType = `${file?.type || ''}`.toLowerCase()
  const fileName = `${file?.name || ''}`.toLowerCase()
  if (mimeType.startsWith('image/')) {
    return 'image'
  }
  if (mimeType.startsWith('video/')) {
    return 'video'
  }
  if (mimeType.startsWith('audio/')) {
    return 'audio'
  }
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName)) {
    return 'image'
  }
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(fileName)) {
    return 'video'
  }
  if (/\.(mp3|wav|aac|ogg|m4a)$/i.test(fileName)) {
    return 'audio'
  }
  return 'document'
}

function formatPendingAttachmentSize(sizeInBytes) {
  const size = Number(sizeInBytes || 0)
  if (!size) {
    return ''
  }
  if (size < 1024) {
    return `${size} B`
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

function describePendingAttachment(file) {
  const kind = getPendingAttachmentKind(file)
  const typeLabel = kind === 'image'
    ? 'Photo'
    : kind === 'video'
      ? 'Video'
      : kind === 'audio'
        ? 'Audio'
        : 'Document'
  const sizeLabel = formatPendingAttachmentSize(file?.size)
  return {
    kind,
    secondaryLabel: sizeLabel ? `${typeLabel} · ${sizeLabel}` : typeLabel,
  }
}

function AttachmentPreview({ attachment }) {
  const fileName = attachment?.fileName || 'Attachment'
  const url = attachment?.previewUrl || attachment?.downloadUrl
  const kind = attachment?.kind || 'file'

  if (!url) {
    return null
  }

  if (kind === 'image') {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={fileName}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  if (kind === 'audio') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: 'rgba(255,255,255,0.7)' }}>
        <audio controls preload="metadata" src={url} style={{ width: '100%' }} />
      </Box>
    )
  }

  if (kind === 'video') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: '#0f172a' }}>
        <video controls preload="metadata" src={url} style={{ width: '100%', maxHeight: 240 }} />
      </Box>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      size="small"
      startIcon={getAttachmentIcon(kind)}
      endIcon={<OpenInNewRoundedIcon />}
      sx={{
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: 2,
        textTransform: 'none',
      }}
    >
      {fileName}
    </Button>
  )
}

function renderWhatsappMarkup(text) {
  if (!text) {
    return null
  }

  const segments = []
  const regex = /(\*[^*]+\*|_[^_]+_|~[^~]+~|```[\s\S]+?```)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        <Fragment key={"plain-" + keyIndex}>
          {text.slice(lastIndex, match.index)}
        </Fragment>,
      )
      keyIndex += 1
    }

    const token = match[0]
    const wrapperKey = "token-" + keyIndex
    if (token.startsWith("```")) {
      segments.push(
        <Box
          component="span"
          key={wrapperKey}
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
            background: "rgba(148, 163, 184, 0.2)",
            borderRadius: 1,
            px: 0.6,
            py: 0.2,
            whiteSpace: "pre-wrap",
            display: "inline-block",
          }}
        >
          {token.slice(3, -3)}
        </Box>,
      )
    } else if (token.startsWith("*")) {
      segments.push(
        <Box component="strong" key={wrapperKey} sx={{ fontWeight: 700 }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith("_")) {
      segments.push(
        <Box component="em" key={wrapperKey} sx={{ fontStyle: "italic" }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith("~")) {
      segments.push(
        <Box component="span" key={wrapperKey} sx={{ textDecoration: "line-through" }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else {
      segments.push(
        <Fragment key={wrapperKey}>
          {token}
        </Fragment>,
      )
    }
    keyIndex += 1
    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    segments.push(
      <Fragment key={"plain-" + keyIndex}>
        {text.slice(lastIndex)}
      </Fragment>,
    )
  }

  return segments
}

function MessageBubble({ item }) {
  const incoming = item?.direction === 'incoming'
  const attachments = item?.attachments || []
  const deliveryMeta = !incoming ? getDeliveryStatusMeta(item?.deliveryStatus) : null

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: incoming ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '96%', md: '88%' },
          px: 1.5,
          py: 1.2,
          borderRadius: 2.5,
          background: incoming
            ? 'rgba(241, 245, 249, 0.96)'
            : 'linear-gradient(135deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.95) 100%)',
          border: incoming
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(22, 163, 74, 0.18)',
          boxShadow: incoming
            ? '0 8px 24px rgba(15, 23, 42, 0.05)'
            : '0 10px 26px rgba(22, 163, 74, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {incoming ? item?.targetDisplayName || 'Recipient' : item?.supportAgentName || 'Support'}
            </Typography>
            <Chip
              size="small"
              label={getTemplateActionFriendlyLabel(item?.actionType, item?.label)}
              color={getTemplateActionChipColor(item?.actionType)}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatThreadTimestamp(item?.createdAt)}
          </Typography>
        </Stack>

        {item?.messagePreview ? (
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: '#111827',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.55,
            }}
          >
            {renderWhatsappMarkup(item.messagePreview)}
          </Typography>
        ) : null}

        {attachments.length > 0 ? (
          <Stack spacing={1} sx={{ mt: item?.messagePreview ? 1.1 : 0.25 }}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                key={`${attachment?.fileId || attachment?.downloadUrl || 'attachment'}-${index}`}
                attachment={attachment}
              />
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
          {attachments.length > 0 ? (
            <Chip
              size="small"
              icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
              label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{ height: 22 }}
            />
          ) : null}
          {deliveryMeta ? (
            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: deliveryMeta.color }}>
              {deliveryMeta.icon}
              <Typography variant="caption" sx={{ color: deliveryMeta.color, fontWeight: 700 }}>
                {deliveryMeta.label}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </Box>
  )
}

function buildContextFromThread(thread) {
  if (!thread) {
    return null
  }

  return {
    patientId: thread?.patientId || undefined,
    practitionerId: thread?.practitionerId || undefined,
    bookedEventId: thread?.bookedEventId || undefined,
    clinicBrandId: thread?.clinicBrandId || undefined,
    clinicId: thread?.clinicId || undefined,
    targetRole: thread?.targetRole || undefined,
    eventType: thread?.eventType || undefined,
  }
}

export default function SupportWhatsappThreadsPanel({
  uiSurface = 'support-floating-threads',
  sx = {},
}) {
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadsError, setThreadsError] = useState('')
  const [threads, setThreads] = useState([])
  const [selectedThreadId, setSelectedThreadId] = useState('')
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState('')
  const [threadState, setThreadState] = useState({
    messages: [],
    templateActions: [],
    freeFormAllowed: false,
    freeFormWindowStartAt: null,
    freeFormWindowEndAt: null,
    targetDisplayName: '',
    targetPhoneNumber: '',
    targetWhatsappNumber: '',
    targetEmail: '',
    targetUserId: '',
    targetActorId: '',
    clinicBrandName: '',
    sharedWhatsappPatientIds: [],
  })
  const [capabilities, setCapabilities] = useState(null)
  const [selectedTemplateAction, setSelectedTemplateAction] = useState('')
  const [messageDraft, setMessageDraft] = useState('')
  const [messageSending, setMessageSending] = useState(false)
  const [templateSending, setTemplateSending] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [attachmentInputKey, setAttachmentInputKey] = useState(0)
  const [attachmentMenuAnchorEl, setAttachmentMenuAnchorEl] = useState(null)
  const [attachmentPickerAccept, setAttachmentPickerAccept] = useState('*/*')
  const [attachmentPickerMode, setAttachmentPickerMode] = useState('all')
  const [showTemplatePanel, setShowTemplatePanel] = useState(true)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const messagesBottomRef = useRef(null)
  const attachmentInputRef = useRef(null)

  const selectedThread = useMemo(
    () => threads.find((item) => item?.threadId === selectedThreadId) || null,
    [selectedThreadId, threads],
  )
  const selectedContext = useMemo(() => buildContextFromThread(selectedThread), [selectedThread])

  const freeFormWindowLabel = useMemo(() => {
    const startLabel = formatThreadTimestamp(threadState?.freeFormWindowStartAt)
    const endLabel = formatThreadTimestamp(threadState?.freeFormWindowEndAt)
    if (!startLabel || !endLabel) {
      return ""
    }
    return startLabel + " to " + endLabel
  }, [threadState?.freeFormWindowStartAt, threadState?.freeFormWindowEndAt])

  function resetAttachmentPicker() {
    setAttachmentFiles([])
    setAttachmentInputKey((currentValue) => currentValue + 1)
  }

  const scrollMessagesToBottom = useCallback((behavior = 'smooth') => {
    window.requestAnimationFrame(() => {
      messagesBottomRef.current?.scrollIntoView({ behavior, block: 'end' })
    })
  }, [])
  const refreshThreads = useCallback(async (preferredThreadId) => {
    setThreadsLoading(true)
    const result = await getSupportAgentWhatsappThreads()
    setThreadsLoading(false)

    if (!result?.ok) {
      setThreads([])
      setThreadsError(result?.message || 'Unable to load support WhatsApp threads.')
      setSelectedThreadId('')
      return null
    }

    const nextThreads = result?.threads || []
    setThreads(nextThreads)
    setThreadsError('')
    setSelectedThreadId((currentValue) => {
      const requestedThreadId = preferredThreadId || currentValue
      if (requestedThreadId && nextThreads.some((item) => item?.threadId === requestedThreadId)) {
        return requestedThreadId
      }
      return nextThreads[0]?.threadId || ''
    })
    return nextThreads
  }, [])

  useEffect(() => {
    refreshThreads()
  }, [refreshThreads])

  useEffect(() => {
    let refreshTimeoutId = null

    const removeListener = addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.supportSignalingEvent,
      handler: (event) => {
        const detail = event?.detail
        if (detail?.command !== SIGNALING_COMMANDS.SUPPORT_WHATSAPP_THREAD_UPDATED) {
          return
        }

        const payload = detail?.payload || {}
        if (refreshTimeoutId) {
          window.clearTimeout(refreshTimeoutId)
        }

        refreshTimeoutId = window.setTimeout(() => {
          refreshThreads(payload?.threadId || selectedThreadId)
        }, 250)
      },
    })

    return () => {
      removeListener()
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId)
      }
    }
  }, [refreshThreads, selectedThreadId])

  useEffect(() => {
    let active = true

    async function loadThreadDetails() {
      if (!selectedContext) {
        setCapabilities(null)
        setThreadState({
          messages: [],
          templateActions: [],
          freeFormAllowed: false,
          freeFormWindowStartAt: null,
          freeFormWindowEndAt: null,
          targetDisplayName: '',
          targetPhoneNumber: '',
          targetWhatsappNumber: '',
          targetEmail: '',
          targetUserId: '',
          targetActorId: '',
          clinicBrandName: '',
          sharedWhatsappPatientIds: [],
        })
        setDetailsError('')
        resetAttachmentPicker()
        return
      }

      setDetailsLoading(true)
      const [capabilityResult, threadResult] = await Promise.all([
        getSupportAgentContactCapabilities(selectedContext),
        getSupportAgentWhatsappThread(selectedContext),
      ])

      if (!active) {
        return
      }

      if (capabilityResult?.ok) {
        setCapabilities(capabilityResult)
      } else {
        setCapabilities(null)
      }

      if (threadResult?.ok) {
        setThreadState({
          messages: threadResult?.messages || [],
          templateActions: threadResult?.templateActions || [],
          freeFormAllowed: !!threadResult?.freeFormAllowed,
          freeFormWindowStartAt: threadResult?.freeFormWindowStartAt || null,
          freeFormWindowEndAt: threadResult?.freeFormWindowEndAt || null,
          targetDisplayName:
            threadResult?.targetDisplayName || capabilityResult?.targetDisplayName || selectedThread?.targetDisplayName || '',
          targetPhoneNumber: threadResult?.targetPhoneNumber || '',
          targetWhatsappNumber: threadResult?.targetWhatsappNumber || '',
          targetEmail: threadResult?.targetEmail || '',
          targetUserId: threadResult?.targetUserId || '',
          targetActorId: threadResult?.targetActorId || '',
          clinicBrandName: threadResult?.clinicBrandName || '',
          sharedWhatsappPatientIds: threadResult?.sharedWhatsappPatientIds || [],
        })
        setDetailsError('')
        setSelectedTemplateAction((currentValue) => {
          const nextTemplateIds = (threadResult?.templateActions || []).map((item) => item?.id)
          if (currentValue && nextTemplateIds.includes(currentValue)) {
            return currentValue
          }
          return threadResult?.templateActions?.[0]?.id || ''
        })
      } else {
        setThreadState({
          messages: [],
          templateActions: [],
          freeFormAllowed: false,
          freeFormWindowStartAt: null,
          freeFormWindowEndAt: null,
          targetDisplayName: selectedThread?.targetDisplayName || '',
        })
        setDetailsError(threadResult?.message || 'Unable to load this WhatsApp conversation.')
      }
      setDetailsLoading(false)
    }

    loadThreadDetails()

    return () => {
      active = false
    }
  }, [selectedContext, selectedThread?.targetDisplayName])

  async function handleSendTemplate() {
    if (!selectedContext || !selectedTemplateAction) {
      toast.error('Choose a conversation and template action first.')
      return
    }

    setTemplateSending(true)
    const result = await sendSupportAgentWhatsappTemplate(selectedContext, selectedTemplateAction)
    setTemplateSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp template.')
      return
    }

    toast.success('Template sent on WhatsApp.')
    await refreshThreads(selectedThreadId)
  }

  async function handleSendMessage() {
    if (!selectedContext || (!messageDraft.trim() && attachmentFiles.length === 0)) {
      toast.error('Enter a message or attach a file first.')
      return
    }

    setMessageSending(true)
    const result = attachmentFiles.length > 0
      ? await sendSupportAgentWhatsappAttachment(selectedContext, messageDraft.trim(), attachmentFiles)
      : await sendSupportAgentWhatsappMessage(selectedContext, messageDraft.trim())
    setMessageSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp message.')
      return
    }

    toast.success(attachmentFiles.length > 0 ? 'WhatsApp attachment sent.' : 'WhatsApp message sent.')
    setMessageDraft('')
    resetAttachmentPicker()
    await refreshThreads(selectedThreadId)
  }

  async function handlePrepareEmail() {
    if (!selectedContext) {
      return
    }

    setEmailLoading(true)
    const result = await prepareSupportAgentEmail(selectedContext)
    setEmailLoading(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to prepare the email draft.')
      return
    }

    if (!result?.data?.mailtoUri) {
      toast.error('Email draft URL was not returned.')
      return
    }

    window.location.href = result.data.mailtoUri
  }

  function handleAttachmentSelection(event) {
    const nextFiles = Array.from(event?.target?.files || [])
    if (event?.target) {
      event.target.value = ''
    }
    if (nextFiles.length === 0) {
      return
    }
    setAttachmentFiles((currentValue) => [...currentValue, ...nextFiles])
  }

  function removeAttachmentAt(indexToRemove) {
    setAttachmentFiles((currentValue) => currentValue.filter((_, index) => index !== indexToRemove))
  }

  function openAttachmentMenu(event) {
    setAttachmentMenuAnchorEl(event?.currentTarget || null)
  }

  function closeAttachmentMenu() {
    setAttachmentMenuAnchorEl(null)
  }

  function triggerAttachmentPicker(option) {
    if (!option) {
      return
    }
    setAttachmentPickerAccept(option.accept || '*/*')
    setAttachmentPickerMode(option.id || 'all')
    setAttachmentMenuAnchorEl(null)
    window.setTimeout(() => {
      attachmentInputRef.current?.click()
    }, 40)
  }

  const channels = capabilities?.channels || {}
  const phoneChannel = channels?.phone || {}
  const emailChannel = channels?.email || {}
  const canCall = !!phoneChannel?.enabled
  const canEmail = !!emailChannel?.enabled
  const composerDisabled = !threadState?.freeFormAllowed || messageSending
  const sendDisabled = composerDisabled || (!messageDraft.trim() && attachmentFiles.length === 0)
  const attachmentMenuOpen = Boolean(attachmentMenuAnchorEl)
  const latestMessageSignature = useMemo(() => {
    const lastMessage = threadState?.messages?.[threadState.messages.length - 1]
    return `${selectedThreadId || ''}::${threadState?.messages?.length || 0}::${lastMessage?.id || lastMessage?.messageSid || lastMessage?.createdAt || ''}`
  }, [selectedThreadId, threadState?.messages])

  useEffect(() => {
    if (!selectedThreadId || detailsLoading) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      scrollMessagesToBottom(threadState?.messages?.length > 0 ? 'smooth' : 'auto')
    }, 80)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [detailsLoading, latestMessageSignature, scrollMessagesToBottom, selectedThreadId, threadState?.messages?.length])

  useEffect(() => {
    if (!selectedThreadId) {
      return
    }

    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return
      }
      refreshThreads(selectedThreadId)
    }, 7000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [refreshThreads, selectedThreadId])

  useEffect(() => {
    setShowTemplatePanel(!threadState?.freeFormAllowed)
  }, [threadState?.freeFormAllowed, selectedThreadId])

  const targetPhone = threadState?.targetPhoneNumber || selectedThread?.targetPhoneNumber || ''
  const targetWhatsapp = threadState?.targetWhatsappNumber || selectedThread?.targetWhatsappNumber || ''
  const targetEmail = threadState?.targetEmail || selectedThread?.targetEmail || ''
  const hasDetails = Boolean(targetPhone || targetWhatsapp || targetEmail || threadState?.targetUserId || threadState?.targetActorId)

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) {
      return
    }
    event.preventDefault()
    if (!sendDisabled) {
      handleSendMessage()
    }
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: { xs: 1, md: 1.5 },
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            Support Threads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            Live WhatsApp conversations with delivery receipts, files, and quick call or email actions.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          startIcon={threadsLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshRoundedIcon />}
          onClick={() => refreshThreads(selectedThreadId)}
          disabled={threadsLoading}
          sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Stack>

      {threadsError ? <Alert severity="warning">{threadsError}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(226, 232, 240, 1)',
          borderRadius: 4,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
          gridTemplateRows: { xs: selectedThread ? '240px minmax(0, 1fr)' : '1fr', md: '1fr' },
          minHeight: 0,
          flex: 1,
          background: '#ffffff',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
            borderBottom: { xs: '1px solid rgba(226, 232, 240, 1)', md: 'none' },
            background: 'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Box sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid rgba(226, 232, 240, 1)', background: 'rgba(255,255,255,0.9)' }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                    Conversations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recent patient and practitioner threads
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  icon={<HistoryEduRoundedIcon />}
                  label={`${threads.length}`}
                  color="primary"
                  variant="outlined"
                />
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${threads.length} active`} color="primary" variant="outlined" />
                {selectedThread?.freeFormAllowed ? (
                  <Chip size="small" color="success" label="Reply window open" />
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.9,
            }}
          >
            {threadsLoading && threads.length === 0 ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}

            {!threadsLoading && threads.length === 0 ? (
              <Alert severity="info">
                Start a WhatsApp conversation from a patient or practitioner record and it will show up here.
              </Alert>
            ) : null}

            {threads.map((thread) => {
              const selected = thread?.threadId === selectedThreadId
              const deliveryMeta = thread?.lastDirection !== 'incoming'
                ? getDeliveryStatusMeta(thread?.lastDeliveryStatus || thread?.lastStatus)
                : null
              return (
                <Button
                  key={thread?.threadId}
                  variant={selected ? 'contained' : 'outlined'}
                  color={selected ? 'primary' : 'inherit'}
                  onClick={() => setSelectedThreadId(thread?.threadId)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: 3,
                    px: 1.35,
                    py: 1.2,
                    alignItems: 'flex-start',
                    background: selected
                      ? 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)'
                      : '#ffffff',
                    color: selected ? '#ffffff' : '#0f172a',
                    borderColor: selected ? 'transparent' : 'rgba(226, 232, 240, 1)',
                    boxShadow: selected
                      ? '0 18px 34px rgba(37, 99, 235, 0.26)'
                      : '0 8px 20px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  <Stack spacing={0.7} sx={{ width: '100%', textAlign: 'left' }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.2, pr: 1 }}>
                        {thread?.targetDisplayName || 'Recipient'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: selected ? 'rgba(255,255,255,0.82)' : 'text.secondary', flexShrink: 0 }}
                      >
                        {formatThreadTimestamp(thread?.lastCreatedAt)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                      {thread?.eventType ? (
                        <Chip
                          size="small"
                          label={thread.eventType}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={{
                            height: 22,
                            color: selected ? '#0f172a' : undefined,
                            background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                          }}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        label={getTemplateActionFriendlyLabel(thread?.lastActionType, thread?.lastActionLabel)}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          height: 22,
                          color: selected ? '#0f172a' : undefined,
                          background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                        }}
                      />
                      {thread?.attachmentCount ? (
                        <Chip
                          size="small"
                          icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
                          label={`${thread.attachmentCount}`}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={{
                            height: 22,
                            color: selected ? '#0f172a' : undefined,
                            background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                          }}
                        />
                      ) : null}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        color: selected ? 'rgba(255,255,255,0.92)' : '#475569',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.45,
                      }}
                    >
                      {thread?.lastMessagePreview || 'No message preview available yet.'}
                    </Typography>
                    {deliveryMeta ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: selected ? '#dbeafe' : deliveryMeta.color }}>
                        {deliveryMeta.icon}
                        <Typography variant="caption" sx={{ color: selected ? '#dbeafe' : deliveryMeta.color, fontWeight: 700 }}>
                          {deliveryMeta.label}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </Button>
              )
            })}
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
          }}
        >
          {selectedThread ? (
            <>
              <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid rgba(226, 232, 240, 1)' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {threadState?.targetDisplayName || selectedThread?.targetDisplayName || 'Recipient'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          {selectedThread?.targetRole || 'contact'}
                          {selectedThread?.practitionerName ? ` • ${selectedThread.practitionerName}` : ''}
                        </Typography>
                        {targetWhatsapp ? (
                          <Chip
                            size="small"
                            label={targetWhatsapp}
                            onClick={() => setDetailsOpen(true)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ) : null}
                        {!targetWhatsapp && targetPhone ? (
                          <Chip
                            size="small"
                            label={targetPhone}
                            onClick={() => setDetailsOpen(true)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ) : null}
                        {hasDetails ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setDetailsOpen(true)}
                            sx={{ textTransform: 'none', px: 0.5 }}
                          >
                            View details
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                      {canCall ? (
                        <PortalVoiceCallButton
                          actorRole="support-agent"
                          patientId={selectedContext?.patientId}
                          practitionerId={selectedContext?.practitionerId}
                          bookedEventId={selectedContext?.bookedEventId}
                          clinicBrandId={selectedContext?.clinicBrandId}
                          clinicId={selectedContext?.clinicId}
                          targetRole={selectedContext?.targetRole}
                          eventType={selectedContext?.eventType}
                          uiSurface={uiSurface}
                          allowedDestinationTypes={[phoneChannel?.destinationType]}
                          autoStartWhenSingleRoute
                          buttonLabel="Call"
                          size="small"
                        />
                      ) : null}
                      {canEmail ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon />}
                          disabled={emailLoading}
                          onClick={handlePrepareEmail}
                        >
                          Email
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {selectedThread?.eventType ? (
                      <Chip size="small" label={selectedThread.eventType} variant="outlined" />
                    ) : null}
                    {threadState?.freeFormAllowed ? (
                      <Chip size="small" color="success" label="Free-form open" />
                    ) : (
                      <Chip size="small" color="warning" label="Template first" />
                    )}
                  </Stack>
                  {freeFormWindowLabel ? (
                    <Typography variant="caption" color="text.secondary">
                      {threadState?.freeFormAllowed ? "Free-form window:" : "Last reply window:"} {freeFormWindowLabel}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>

              {detailsError ? <Alert severity="warning" sx={{ m: 1.5 }}>{detailsError}</Alert> : null}

              {detailsLoading ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', flex: 1 }}>
                  <CircularProgress size={26} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      px: { xs: 1.25, md: 2 },
                      py: 1.5,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                      scrollBehavior: 'smooth',
                      background:
                        'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 1) 18%)',
                    }}
                  >
                    {threadState?.messages?.length > 0 ? (
                      <>
                        {threadState.messages.map((item) => <MessageBubble key={item?.id} item={item} />)}
                        <Box ref={messagesBottomRef} />
                      </>
                    ) : (
                      <Alert severity="info">
                        No messages are recorded for this thread yet.
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  <Box sx={{ px: { xs: 1.25, md: 2 }, py: 1.5, background: '#ffffff' }}>
                    <Stack spacing={1.1}>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.6,
                          borderRadius: 2,
                          background: threadState?.freeFormAllowed
                            ? 'rgba(220, 252, 231, 0.5)'
                            : 'rgba(254, 249, 195, 0.7)',
                          border: threadState?.freeFormAllowed
                            ? '1px solid rgba(34, 197, 94, 0.12)'
                            : '1px solid rgba(245, 158, 11, 0.18)',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography
                            variant="caption"
                            sx={{
                              color: threadState?.freeFormAllowed ? '#166534' : '#92400e',
                              fontWeight: 700,
                              lineHeight: 1.4,
                            }}
                          >
                            {threadState?.freeFormAllowed
                              ? 'Free-form window is open. You can send messages and attachments.'
                              : 'Send an approved opener to start a new WhatsApp conversation.'}
                          </Typography>
                          {threadState?.templateActions?.length > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setShowTemplatePanel((currentValue) => !currentValue)}
                              sx={{ textTransform: 'none' }}
                            >
                              {showTemplatePanel ? 'Hide' : 'Send approved'}
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>

                      {threadState?.templateActions?.length > 0 ? (
                        <Collapse in={showTemplatePanel}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch" sx={{ pt: 0.5 }}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Approved message"
                              value={selectedTemplateAction}
                              onChange={(event) => setSelectedTemplateAction(event.target.value)}
                            >
                              {threadState.templateActions.map((action) => (
                                <MenuItem key={action?.id} value={action?.id}>
                                  {action?.label || action?.id}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Button
                              variant="outlined"
                              color="success"
                              startIcon={templateSending ? <CircularProgress size={14} color="inherit" /> : <WhatsAppIcon />}
                              onClick={handleSendTemplate}
                              disabled={!selectedTemplateAction || templateSending}
                              sx={{ minWidth: { xs: '100%', sm: 92 }, flexShrink: 0 }}
                            >
                              Send
                            </Button>
                          </Stack>
                        </Collapse>
                      ) : null}

                      {attachmentFiles.length > 0 ? (
                        <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.25 }}>
                          {attachmentFiles.map((file, index) => {
                            const attachmentMeta = describePendingAttachment(file)
                            return (
                              <Paper
                                key={`${file?.name || 'file'}-${index}`}
                                elevation={0}
                                sx={{
                                  minWidth: 180,
                                  maxWidth: 260,
                                  px: 1,
                                  py: 0.75,
                                  borderRadius: 2.5,
                                  border: '1px solid rgba(203, 213, 225, 0.9)',
                                  background: '#ffffff',
                                  flexShrink: 0,
                                }}
                              >
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1.5,
                                      display: 'grid',
                                      placeItems: 'center',
                                      background: 'rgba(226, 232, 240, 0.9)',
                                      color: '#0f172a',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {getComposerAttachmentIcon(attachmentMeta.kind)}
                                  </Box>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f172a' }} noWrap>
                                      {file?.name || `Attachment ${index + 1}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {attachmentMeta.secondaryLabel}
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => removeAttachmentAt(index)} sx={{ flexShrink: 0 }}>
                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Stack>
                              </Paper>
                            )
                          })}
                        </Stack>
                      ) : null}

                      <Paper
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 0.5,
                          px: 0.75,
                          py: 0.5,
                          borderRadius: 3,
                          border: '1px solid rgba(203, 213, 225, 0.9)',
                          background: '#ffffff',
                          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <Tooltip title="Attach">
                          <span>
                            <IconButton
                              size="small"
                              onClick={openAttachmentMenu}
                              disabled={composerDisabled}
                              aria-label="Choose attachment type"
                              sx={{
                                width: 38,
                                height: 38,
                                color: '#54656f',
                              }}
                            >
                              <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={6}
                            variant="standard"
                            placeholder={
                              threadState?.freeFormAllowed
                                ? 'Type a message'
                                : 'Send an approved opener first'
                            }
                            value={messageDraft}
                            onChange={(event) => setMessageDraft(event.target.value)}
                            onKeyDown={handleComposerKeyDown}
                            disabled={composerDisabled}
                            InputProps={{ disableUnderline: true }}
                            sx={{
                              '& .MuiInputBase-root': {
                                px: 0.5,
                                py: 0.75,
                                alignItems: 'flex-end',
                                fontSize: '0.95rem',
                                lineHeight: 1.5,
                              },
                              '& textarea': {
                                resize: 'none',
                              },
                            }}
                          />
                        </Box>

                        <Tooltip title={sendDisabled ? 'Type a message or attach a file' : 'Send'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleSendMessage}
                              disabled={sendDisabled}
                              aria-label="Send message"
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: sendDisabled ? 'rgba(148, 163, 184, 0.18)' : '#00a884',
                                color: sendDisabled ? '#94a3b8' : '#ffffff',
                                '&:hover': {
                                  bgcolor: sendDisabled ? 'rgba(148, 163, 184, 0.24)' : '#019270',
                                },
                              }}
                            >
                              {messageSending ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Paper>

                      <input
                        ref={attachmentInputRef}
                        key={`${attachmentInputKey}-${attachmentPickerMode}`}
                        hidden
                        multiple
                        type="file"
                        accept={attachmentPickerAccept}
                        onChange={handleAttachmentSelection}
                      />
                      <Menu
                        anchorEl={attachmentMenuAnchorEl}
                        open={attachmentMenuOpen}
                        onClose={closeAttachmentMenu}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{
                          sx: {
                            mt: -1,
                            borderRadius: 3,
                            minWidth: 250,
                            boxShadow: '0 20px 48px rgba(15, 23, 42, 0.18)',
                            border: '1px solid rgba(226, 232, 240, 0.9)',
                            p: 0.5,
                          },
                        }}
                      >
                        {WHATSAPP_ATTACHMENT_OPTIONS.map((option) => (
                          <MenuItem key={option.id} onClick={() => triggerAttachmentPicker(option)} sx={{ borderRadius: 2, py: 1 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.5,
                                  display: 'grid',
                                  placeItems: 'center',
                                  background: 'rgba(240, 242, 245, 0.95)',
                                  color: '#0f172a',
                                  flexShrink: 0,
                                }}
                              >
                                {getComposerAttachmentIcon(option.kind, 20)}
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                  {option.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.helper}
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Stack>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                Choose a support conversation to continue the chat and trigger call or email from the same place.
              </Alert>
            </Box>
          )}
        </Box>
      </Paper>

      <Drawer
        anchor="right"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 360 }, p: 2.5 },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Contact details
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {threadState?.targetDisplayName || selectedThread?.targetDisplayName || 'Recipient'}
            </Typography>
            <Typography variant="body2">
              Role: {selectedThread?.targetRole || threadState?.targetRole || 'contact'}
            </Typography>
            {targetWhatsapp ? <Typography variant="body2">WhatsApp: {targetWhatsapp}</Typography> : null}
            {targetPhone ? <Typography variant="body2">Phone: {targetPhone}</Typography> : null}
            {targetEmail ? <Typography variant="body2">Email: {targetEmail}</Typography> : null}
            {threadState?.clinicBrandName ? (
              <Typography variant="body2">Clinic brand: {threadState.clinicBrandName}</Typography>
            ) : null}
            {selectedThread?.clinicBrandId ? (
              <Typography variant="body2">Clinic brand ID: {selectedThread.clinicBrandId}</Typography>
            ) : null}
            {selectedThread?.clinicId ? <Typography variant="body2">Clinic ID: {selectedThread.clinicId}</Typography> : null}
            {selectedThread?.bookedEventId ? (
              <Typography variant="body2">Booked event: {selectedThread.bookedEventId}</Typography>
            ) : null}
            {threadState?.targetActorId ? <Typography variant="body2">Target ID: {threadState.targetActorId}</Typography> : null}
            {threadState?.targetUserId ? <Typography variant="body2">User ID: {threadState.targetUserId}</Typography> : null}
            {threadState?.sharedWhatsappPatientIds?.length > 1 ? (
              <Typography variant="body2">
                Linked patients: {threadState.sharedWhatsappPatientIds.join(', ')}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  )
}
